import { AutoSelection, Combobox, EmptySearchResult, LegacyStack, Listbox, Tag, Text } from "@shopify/polaris";
import { useCallback, useMemo, useState } from "react";
import { useAppQuery } from "../../../hooks/index.js";

interface IInventorylocations {
  inventoryLocations: String[];
}

function SelectedLocationsField({ input, labelHidden, disabled, meta: { error } }) {
  const [selectedLocation, setSelectedLocation] = useState<string[]>(input.value || []);
  const [value, setValue] = useState("");
  const [suggestion, setSuggestion] = useState("");

  const { data } = useAppQuery<IInventorylocations, Error>({
    url: "/api/products/inventory-locations",
    reactQueryOptions: {
      staleTime: Infinity
    },
    fetchInit: {}
  });

  const handleActiveOptionChange = useCallback(
    (activeOption: string) => {
      const activeOptionIsAction = activeOption === value;

      if (!activeOptionIsAction && !selectedLocation.includes(activeOption)) {
        setSuggestion(activeOption);
      } else {
        setSuggestion("");
      }
    },
    [value, selectedLocation]
  );

  const updateSelection = useCallback(
    (selected: string) => {
      const nextSelectedTags = new Set([...selectedLocation]);

      if (nextSelectedTags.has(selected)) {
        nextSelectedTags.delete(selected);
      } else {
        nextSelectedTags.add(selected);
      }
      setSelectedLocation([...nextSelectedTags]);
      setValue("");
      setSuggestion("");
      input.onChange([...nextSelectedTags]);
    },
    [selectedLocation, input]
  );

  const removeTag = useCallback(
    (tag: string) => () => {
      updateSelection(tag);
    },
    [updateSelection]
  );

  const getInventoryLocationsTags = useCallback(() => {
    const savedTags = data?.inventoryLocations || [];
    return [...new Set([...savedTags, ...selectedLocation].sort())];
  }, [selectedLocation, data]);

  const formatOptionText = useCallback(
    (option: string) => {
      const trimValue = value.trim().toLocaleLowerCase();
      const matchIndex = option.toLocaleLowerCase().indexOf(trimValue);

      if (!value || matchIndex === -1) return option;

      const start = option.slice(0, matchIndex);
      const highlight = option.slice(matchIndex, matchIndex + trimValue.length);
      const end = option.slice(matchIndex + trimValue.length, option.length);

      return (
        <p>
          {start}
          <Text fontWeight="bold" as="span" variant={"bodyMd"}>
            {highlight}
          </Text>
          {end}
        </p>
      );
    },
    [value]
  );

  const options = useMemo(() => {
    let list;
    const allInventoryLocations = getInventoryLocationsTags();
    const filterRegex = new RegExp(value, "i");

    if (value) {
      list = allInventoryLocations.filter((location) => location.match(filterRegex));
    } else {
      list = allInventoryLocations;
    }

    return [...list];
  }, [value, getInventoryLocationsTags]);

  const verticalContentMarkup =
    selectedLocation?.length > 0 ? (
      <LegacyStack spacing="extraTight" alignment="center">
        {selectedLocation && selectedLocation?.map((location) => (
          <Tag key={`option-${location}`} onRemove={removeTag(location)}>
            {location}
          </Tag>
        ))}
      </LegacyStack>
    ) : null;

  const optionMarkup =
    options.length > 0
      ? options.map((option) => {
        return (
          <Listbox.Option
            key={option}
            value={option}
            selected={selectedLocation.includes(option)}
            accessibilityLabel={option}
          >
            <Listbox.TextOption selected={selectedLocation.includes(option)}>
              {formatOptionText(option)}
            </Listbox.TextOption>
          </Listbox.Option>
        );
      })
      : null;

  const noResults = value && !getInventoryLocationsTags().includes(value);

  const actionMarkup = noResults ? (
    <Listbox.Action value={value}>{`Add "${value}"`}</Listbox.Action>
  ) : null;

  const emptyStateMarkup = optionMarkup ? null : (
    <EmptySearchResult
      title=""
      description={`No locations found matching "${value}"`}
    />
  );

  const listboxMarkup =
    optionMarkup || actionMarkup || emptyStateMarkup ? (
      <Listbox
        autoSelection={AutoSelection.None}
        onSelect={updateSelection}
        onActiveOptionChange={handleActiveOptionChange}
      >
        {actionMarkup}
        {optionMarkup}
      </Listbox>
    ) : null;

  return (
    <Combobox
      allowMultiple
      activator={
        <Combobox.TextField
          autoComplete="off"
          label="Selected inventory locations"
          labelHidden={labelHidden}
          value={value}
          suggestion={suggestion}
          placeholder="Search location"
          verticalContent={verticalContentMarkup}
          onChange={setValue}
          error={error}
        />
      }
    >
      {listboxMarkup}
    </Combobox>
  );
}


export default SelectedLocationsField;